import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient, GraphQLResult } from 'aws-amplify/api';

import msg from './locale';
import {
  AWS_APPSYNC_REGION,
  AWS_APPSYNC_API_URL,
  AWS_IDENTITY_POOL_ID,
  AWS_AUTH_USER_POOL_ID,
  AWS_AUTH_USER_POOL_WEB_CLIENT_ID,
} from './constants';
import operations from './operations';
import { getJWT } from '../auth/utils';
import { Queries, Mutations } from './types';
import { Product } from '../product/types';

export async function hasToken(): Promise<boolean> {
  try {
    // forceRefresh ensures we detect expired sessions accurately:
    // - valid refresh token → Amplify silently refreshes and returns true
    // - expired refresh token → throws → returns false
    // Without forceRefresh, Amplify may return a cached but BFF-rejected token.
    const session = await fetchAuthSession({ forceRefresh: true });
    return Boolean(session.tokens?.accessToken);
  } catch {
    return false;
  }
}

export function configureSession() {
  Amplify.configure(
    {
      Auth: {
        Cognito: {
          identityPoolId: AWS_IDENTITY_POOL_ID,
          userPoolId: AWS_AUTH_USER_POOL_ID,
          userPoolClientId: AWS_AUTH_USER_POOL_WEB_CLIENT_ID,
          allowGuestAccess: true,
        },
      },
      API: {
        GraphQL: {
          endpoint: AWS_APPSYNC_API_URL,
          region: AWS_APPSYNC_REGION,
          defaultAuthMode: 'iam',
        },
      },
    },
    { ssr: true }
  );
}

const client = generateClient();

export async function getBFFData(type: Queries | Mutations, params?: any) {
  try {
    const operation = operations[type];
    if (operation) {
      const headers = {};
      const token = await getJWT();
      if (token) {
        headers['x-cl-token'] = token;
      }

      const response = await client.graphql({ query: operation, variables: params }, headers);
      return { ok: true, data: (response as GraphQLResult).data };
    }
    return { ok: false, data: msg.ERROR_ENV };
  } catch (error) {
    if (error.errors && error.errors.length > 0) {
      return { ok: false, data: error.errors[0].message };
    }
    return { ok: false, data: error };
  }
}

export const fetchProductFromBackend = async (recommendedProducts: string[]) => {
  try {
    const result = recommendedProducts
      .map((item) => {
        const cat = item.split('-');
        if (cat.length < 2) {
          return null;
        }
        return { productRef: cat[0], colorRef: cat[1] };
      })
      .filter((item) => item !== null) as { productRef: string; colorRef: string }[];

    const getAllProductsQueries = result.map((item, i: number) => {
      return `
    getProduct${i}: product(productRef: "${item.productRef}", colorRef: "${item.colorRef}") {
        productRef
        productName
        productDescription
        productComposition
        status
        eReservation
        colorRef
        colorLabel
        realColorRef
        detailedColorLabel
        cleaningAdvice
        originalPrice
        storePrice
        promotionPercentage
        promotionLabel
        promoLongLabel
        promoBColor
        promoTColor
        isOutlet
        images
        collectionName
        crossSellType
        categories {
          lvl0
          lvl1
          lvl2
          lvl3
        }
        colorVariants {
          colorRef
          colorLabel
          realColorRef
          detailedColorLabel
          status
          originalPrice
          storePrice
          promotionPercentage
          promotionLabel
          promoLongLabel
          promoBColor
          promoTColor
          label1
          label2
        }
        sizeVariants {
          sku
          originalPrice
          storePrice
          promotionPercentage
          stockQuantity
          cupSize
          bandSize
          size
          storesAvailable {
            quantity
            store
          }
        }
        model {
          position
          name
          size
        }
        moreDetails
        videoUrl
        isDigital
        contentRecycledMaterials
        recyclability
        rejectsMicrofibers
        toxicity
        traceability
        tax
        label1
        label2
      }
  `;
    });

    const results = (await client.graphql({
      query: `
        query getAllProducts {
          ${getAllProductsQueries}
        }
    `,
    })) as { data: { [key: string]: Product } };
    const arrayOfresults = [...Object.values(results.data)];

    return { ok: true, data: arrayOfresults };
  } catch (error) {
    if (!error.errors?.length) {
      return { ok: false, error };
    }
    if (!error.data) {
      return { ok: false, error: error.errors as string[] };
    }
    const dataArray = [...Object.values(error.data)];

    const validProduct = dataArray.filter((item) => item !== null);

    return validProduct.length > 0
      ? { ok: true, data: validProduct as Product[] }
      : { ok: false, error: error.errors as string[] };
  }
};
